import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { client } from "../../../../App";
import {
  handlerErrors,
  isObject,
  standardizeGraphQLError,
} from "../../../../helpers/helpers";
import {
  CONSUMPTION_READING,
  getEvictionForMutation,
} from "../../../../helpers/mutations";
import { hideLoading, showLoading } from "../../../../reducers/app";
import ContractSelect from "../../../common/ContractsSelect/ContractsSelect";
import Hero from "../../../common/Hero/Hero";
import Modal from "../../../common/Modal/Modal";
import SelfReadModal from "../../../common/SelfReadModal/SelfReadModal";

import { CaseForms } from "../../../../helpers/enums";
import "./SelfRead.scss";

const AddSelfReadPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showReading, setShowReading] = useState(false);
  const [genericError, setGenericError] = useState(null);
  const [flowFinished, setFlowFinished] = useState(false);

  const current = useSelector((state) => state.contracts.current);

  const currentContract = current ? current.id : null;

  let schemaValidation = () => {
    return Yup.object().shape({
      reading: Yup.string()
        .required(t("selfread.add.form.required"))
        .max(10, t("selfread.max.value"))
        .matches("^[0-9]*$", {
          message: t("selfread.add.form.typeError"),
        }),
    });
  };

  const handleSubmit = async (value, props) => {
    if (currentContract) {
      dispatch(showLoading());
      try {
        const data = await client().mutate({
          mutation: CONSUMPTION_READING,
          update: (cache) => {
                const fieldsToEvict =
                  getEvictionForMutation("consumptionReading");
                fieldsToEvict.forEach((field) =>
                  cache.evict({ fieldName: field })
                );
                cache.gc();
              },
          variables: {
            req: {
              contractId: currentContract,
              reading: Number(value.reading),
            },
            headers: {
              "x-amzn-RequestId": "yes", //your custom header
            },
          },
        });
        if (data) {
          dispatch(hideLoading());
          setFlowFinished(true);
        }
        setGenericError(null);
        window.dataLayer.push({
          event: "pratica",
          tipo: CaseForms.Autolettura,
          step: "invio",
          esito: "OK",
        });
      } catch (e) {
        const { setErrors } = props;
        const extractErr = standardizeGraphQLError(e);
        if (extractErr && extractErr.error) {
          setGenericError(extractErr.error);
        } else if (extractErr) {
          setGenericError(extractErr);
        } else {
          handlerErrors(e, setErrors, setGenericError);
        }
        dispatch(hideLoading());
        window.dataLayer.push({
          event: "pratica",
          tipo: CaseForms.Autolettura,
          step: "invio",
          esito: "KO",
        });
      }
    }
  };

  // const genericErrorOn = genericError
  //   ? "submit-holder col-lg-6 col-md-12"
  //   : "submit-holder col-lg-6 col-md-12 offset-lg-6";

  const handleGenericError = () => {
    let formattedError;
    let error = isObject(genericError)
      ? genericError[Object.keys(genericError)[0]]
      : genericError;
    if (
      Array.isArray(genericError) &&
      genericError[0] &&
      "reading" in genericError[0]
    ) {
      formattedError = t(`${genericError[0].reading.props.i18nKey}`);
    } else if (isObject(genericError)) {
      formattedError = t(`self.read.error.value`);
    } else {
      formattedError = error.includes("_")
        ? t(`associate-contact.${error}`)
        : error.includes(".")
        ? t(`${error}`)
        : error;
    }

    return (
      <div className={"row"}>
        <div className={"container"}>
          <div className="col-sm-12">
            <div
              className={"generic-error d-flex justify-content-start"}
              role="alert"
              id="selfreading-error-message"
            >
              <i
                className="gc-icon-functional-attention-1-col"
                aria-hidden="true"
              />
              <p>{formattedError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const closeModalReading = (e) => {
    e.preventDefault();
    document.body.classList.remove("overflow-hidden");
    setShowReading(false);
  };

  const showModalReading = (e) => {
    e.preventDefault();
    document.body.classList.add("overflow-hidden");
    setShowReading(true);
  };

  const handleUpdate = (val) => {
    if (val !== current.id) setGenericError(null);
  };

  return (
    <div className="AddSelfReadPage" data-testid="AddSelfReadPage">
      <Hero
        backTo={"/"}
        backToText={t("common.back.home")}
        title={t("selfread.add.heroTitle")}
        helpText={t("selfread.help")}
        description={
          <button onClick={showModalReading} className="hero-text">
            {t("selfread.add.description.link")}
          </button>
        }
      />

      <div className={"container"}>
        <Modal
          show={flowFinished}
          onClose={() => setFlowFinished(false)}
          description={t("selfread.success")}
          type={"link"}
          to={"/"}
          linkTitle={t("common.goToDashboard")}
        />
        <div className="wrapper">
          <Formik
            initialValues={{
              reading: "",
            }}
            validationSchema={schemaValidation}
            onSubmit={(value, props) => handleSubmit(value, props)}
          >
            {({ errors, touched }) => (
              <Form noValidate>
                <div className="mt-5 row">
                  <div className="col-xs-12 col-md-12 text-left">
                    <p
                      className={
                        "case-step-mandatory-fields-legend paragraph-small-fs9"
                      }
                    >
                      {t("mandatory.fields.legend")}
                    </p>
                    <div className={"row"}>
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group contract-select-wrapper">
                          <ContractSelect
                            excludeInactives={true}
                            onUpdate={handleUpdate}
                            label={t("selfread.add.contract")}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label
                            className="paragraph-small-fs9"
                            htmlFor="reading"
                          >
                            {t("selfread.add.form.valoreLettura.label")}
                          </label>
                          <Field
                            title="reading"
                            id="reading"
                            name={"reading"}
                            type="text"
                            className={
                              errors.reading && touched.reading
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            aria-describedby={
                              errors.reading && touched.reading
                                ? "reading-error"
                                : null
                            }
                            required={true}
                            placeholder={t(
                              "selfread.add.form.valoreLettura.placeholder"
                            )}
                          />
                          {errors.reading && touched.reading ? (
                            <p
                              className="form-error-text paragraph-small-form"
                              role="alert"
                              id={"reading-error"}
                            >
                              {errors.reading}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      {/* {genericError && (
                          <div className="col-lg-6 col-md-12">
                            <div
                              className={
                                "generic-error d-flex justify-content-start"
                              }
                            >
                              <i className="gc-icon-functional-attention-1-col"
                                aria-hidden="true"
                              />
                              <p>{genericError}</p>
                            </div>
                          </div>
                        )} */}

                      <div className={"submit-holder col-lg-12 col-md-12 "}>
                        <div className="form-group">
                          <label>&nbsp;</label>
                          <button
                            type="submit"
                            className={"btn btn-primary"}
                            aria-describedby="selfreading-error-message"
                          >
                            {t("selfread.add.form.submit")}
                          </button>
                        </div>
                      </div>
                      {genericError && handleGenericError()}
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      {showReading && <SelfReadModal close={closeModalReading} />}
    </div>
  );
};

export default AddSelfReadPage;
